import { handleResponseOld, makeDelete, makeGet, makePost } from './helpers';

/**
 * @category Messaging API
 * @see addAttachment
 */
type AddAttachmentParams = {
    attachments: Array<any>;
    authToken: string;
    bidderId: number;
    conversationId: number;
    deployment: string;
    draftId: number;
    messageBody: string;
    sellerId?: number;
};
/**
 * Attach a file to message for bidderId
 * @function addAttachment
 * @category Messaging API
 * @param {AddAttachmentParams}
 * @see https://messaging-api-DEPLOYMENT.liveauctioneers.com/messaging/bidders/${bidderId}/attach
 */
const addAttachment = ({
    attachments = [],
    authToken,
    bidderId,
    conversationId,
    deployment,
    draftId,
    messageBody,
    sellerId,
}: AddAttachmentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MESSAGING-ATTACHMENTS-API>bidders/${bidderId}/attach`,
        });
        attachments.forEach((file) => {
            request.attach(file.name, file);
        });
        request.field('message', JSON.stringify({ conversationId, draftId, messageBody, sellerId }));
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Messaging API
 * @see startConversationWithAttachment
 */
type StartConversationWithAttachmentParams = {
    attachments: Array<any>;
    authToken: string;
    bidderId: number;
    deployment: string;
    messageBody: string;
    sellerId: number;
    subject: string;
};
/**
 * Start a conversation with attachment(s)
 * @function startConversationWithAttachment
 * @category Messaging API
 * @param {StartConversationWithAttachmentParams}
 * @see https://messaging-api-DEPLOYMENT.liveauctioneers.com/messaging/bidders/${bidderId}/messages
 */
const startConversationWithAttachment = ({
    attachments = [],
    authToken,
    bidderId,
    deployment,
    messageBody,
    sellerId,
    subject,
}: StartConversationWithAttachmentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment: deployment,
            path: `<MESSAGING-ATTACHMENTS-API>bidders/${bidderId}/messages`,
        });
        attachments.forEach((file) => {
            request.attach(file.name, file);
        });
        request.field('message', JSON.stringify({ houseId: sellerId, messageBody, subject }));
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Messaging API
 * @see deleteDraftAttachment
 */
type DeleteDraftAttachmentParams = {
    attachmentId: number;
    authToken: string;
    bidderId: number;
    deployment: string;
};
/**
 * Deletes a draft attachment by bidder and attachment ID
 * @function deleteDraftAttachment
 * @category Messaging API
 * @param {DeleteDraftAttachmentParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/messaging/bidders/${bidderId}/attachment/${attachmentId}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/77oixlnps8/resources/epl5ig/methods/DELETE
 * @see https://github.com/LIVEauctioneers/messaging-api/tree/master/functions/BidderDeleteAttachment-messaging-api
 */
const deleteDraftAttachment = ({ attachmentId, authToken, bidderId, deployment }: DeleteDraftAttachmentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeDelete({
            authToken,
            deployment,
            path: `<MESSAGING-API>bidders/${bidderId}/attachment/${attachmentId}`,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Messaging API
 * @see fetchConversationMessages
 */
type FetchConversationMessagesParams = {
    authToken: string;
    bidderId: number;
    conversationId: number;
    deployment: string;
};
/**
 * Gets all messages for given conversation and bidder Ids
 * @function fetchConversationMessages
 * @category Messaging API
 * @param {FetchConversationMessages}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/messaging/bidders/${bidderId}/messages
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/77oixlnps8/resources/lmdsx1/methods/GET
 * @see https://github.com/LIVEauctioneers/messaging-api/tree/master/functions/GetMessages-messaging-api
 */
const fetchConversationMessages = ({
    authToken,
    bidderId,
    conversationId,
    deployment,
}: FetchConversationMessagesParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: `<MESSAGING-API>bidders/${bidderId}/messages`,
        });
        request.query({ conversationId });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Messaging API
 * @see sendConversationMessage
 */
type SendConversationMessageParams = {
    authToken: string;
    bidderId: number;
    conversationId: number;
    deployment: string;
    draftId?: number;
    messageBody: any;
};
/**
 * Sends a conversation message to conversation for given bidderId
 * @function sendConversationMessage
 * @category Messaging API
 * @param {SendConversationMessageParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/messaging/bidders/${bidderId}/messages
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/77oixlnps8/resources/lmdsx1/methods/POST
 * @see https://github.com/LIVEauctioneers/messaging-api/tree/master/functions/SendMessage-messaging-api
 */
const sendConversationMessage = ({
    authToken,
    bidderId,
    conversationId,
    deployment,
    draftId,
    messageBody,
}: SendConversationMessageParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MESSAGING-API>bidders/${bidderId}/messages`,
        });
        request.query({ conversationId });
        request.send({
            conversationId,
            draftId,
            messageBody,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });
/**
 * @category Messaging API
 * @see Messaging API
 */
type StartConversationMessageParams = {
    authToken: string;
    bidderId: number;
    catalogId: number;
    deployment: string;
    houseId: number;
    lotId?: number;
    messageBody: any;
    subject?: string;
};
/**
 * Creates a new conversation message for given bidderId and details
 * @function startConversationMessage
 * @category Messaging API
 * @param {StartConversationMessageParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/messaging/bidders/${bidderId}/messages
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/77oixlnps8/resources/lmdsx1/methods/POST
 * @see https://github.com/LIVEauctioneers/messaging-api/tree/master/functions/SendMessage-messaging-api
 */
const startConversationMessage = ({
    authToken,
    bidderId,
    catalogId,
    deployment,
    houseId,
    lotId,
    messageBody,
    subject,
}: StartConversationMessageParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MESSAGING-API>bidders/${bidderId}/messages`,
        });
        request.send({
            catalogId,
            houseId,
            lotId,
            messageBody,
            recipient: 'house',
            subject,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    addAttachment,
    deleteDraftAttachment,
    fetchConversationMessages,
    sendConversationMessage,
    startConversationMessage,
    startConversationWithAttachment,
};
